// CV API dashboard — mirror traffic stats + recent-leads table.
//
// Controls (shared by the stat cards AND the leads table):
//   - API-key dropdown: "All" + each mirror client by friendly name.
//   - Time selection: presets (15m … 30d) + a custom start/end date range.
// Both re-query on change. The whole page AUTO-UPDATES via polling (~15s) —
// stats + leads refresh in place, no page reload. Polling pauses while the tab
// is hidden (visibilitychange) to save cost.

import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import {
  api,
  type DebtMixResponse,
  type LeadEntry,
  type MirrorClientListItem,
  type MirrorDashboardResponse,
  type TimeSel,
} from "../lib/api";
import {
  buttonStyle,
  Card,
  EmptyState,
  PageHeader,
  Pill,
  Skeleton,
  StatCard,
  tokens,
} from "../lib/theme";

const POLL_MS = 15_000;
const LEADS_LIMIT = 100;

// "Today" is the special sentinel: its range is [PT midnight today, now] and it
// is the DEFAULT selected preset on load (MIS-972). The other presets stay
// rolling-window seconds.
const TODAY_PT = -1;

const PRESETS: Array<{ label: string; sec: number }> = [
  { label: "Today (PT)", sec: TODAY_PT }, // special: since PT (America/Los_Angeles) midnight
  { label: "Last 15 min", sec: 15 * 60 },
  { label: "Last 30 min", sec: 30 * 60 },
  { label: "Last hour", sec: 3600 },
  { label: "Last 3 hours", sec: 3 * 3600 },
  { label: "Last 6 hours", sec: 6 * 3600 },
  { label: "Last 24 hours", sec: 24 * 3600 },
  { label: "Last 7 days", sec: 7 * 24 * 3600 },
  { label: "Last 30 days", sec: 30 * 24 * 3600 },
];

function fmtPct(n: number): string {
  return `${(n * 100).toFixed(1)}%`;
}
function fmtCount(n: number): string {
  return n.toLocaleString();
}
function fmtDateTime(iso: string | null): string {
  if (!iso) return "—";
  // Render in Pacific (PT) so the leads table matches the PT reporting model
  // (MIS-972); the trailing "PT" disambiguates the wall-clock for the operator.
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return (
    d.toLocaleString([], {
      timeZone: "America/Los_Angeles",
      month: "short",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    }) + " PT"
  );
}

/**
 * Epoch SECONDS of the most recent America/Los_Angeles (Pacific) midnight at or
 * before `nowMs` (the start of "today" in PT). DST-correct on EVERY day,
 * INCLUDING the two transition days: we derive the PT CALENDAR DATE for `nowMs`,
 * then resolve the exact instant of 00:00:00 on that PT date by measuring PT's
 * actual UTC offset at that instant via Intl (never a fixed -7/-8, and never the
 * "subtract elapsed wall-clock seconds" trick that is off by 1h on a 23h/25h
 * transition day).
 */
function ptMidnightTodaySec(nowMs: number = Date.now()): number {
  const TZ = "America/Los_Angeles";
  const dateParts = new Intl.DateTimeFormat("en-US", {
    timeZone: TZ,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(new Date(nowMs));
  const dp = (t: string): number =>
    Number.parseInt(dateParts.find((p) => p.type === t)?.value ?? "0", 10);
  const y = dp("year");
  const mo = dp("month");
  const d = dp("day");

  const offsetMinutesAt = (ms: number): number => {
    const p = new Intl.DateTimeFormat("en-US", {
      timeZone: TZ,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: false,
    }).formatToParts(new Date(ms));
    const v = (t: string): number =>
      Number.parseInt(p.find((x) => x.type === t)?.value ?? "0", 10);
    const asUtc = Date.UTC(
      v("year"),
      v("month") - 1,
      v("day"),
      v("hour") % 24,
      v("minute"),
      v("second"),
    );
    return Math.round((asUtc - ms) / 60000);
  };

  const guessUtcMs = Date.UTC(y, mo - 1, d, 0, 0, 0);
  const off1 = offsetMinutesAt(nowMs);
  let midnightMs = guessUtcMs - off1 * 60000;
  const off2 = offsetMinutesAt(midnightMs);
  if (off2 !== off1) {
    midnightMs = guessUtcMs - off2 * 60000;
  }
  return Math.floor(midnightMs / 1000);
}

/** Resolve the selected preset/custom into a TimeSel for the API. */
function toTimeSel(
  presetSec: number,
  custom: { start: string; end: string } | null,
): TimeSel {
  if (custom && custom.start && custom.end) {
    const startSec = Math.floor(new Date(custom.start + "T00:00:00Z").getTime() / 1000);
    const endSec = Math.floor(new Date(custom.end + "T23:59:59Z").getTime() / 1000);
    return { kind: "range", startSec, endSec };
  }
  if (presetSec === TODAY_PT) {
    // "Today (PT)" — [PT midnight today, now], epoch seconds (wire format).
    const now = Date.now();
    return {
      kind: "range",
      startSec: ptMidnightTodaySec(now),
      endSec: Math.floor(now / 1000),
    };
  }
  return { kind: "window", windowSec: presetSec };
}

export default function MirrorDashboard() {
  const [clients, setClients] = useState<MirrorClientListItem[]>([]);
  const [clientId, setClientId] = useState<string>("all");
  const [presetSec, setPresetSec] = useState<number>(TODAY_PT); // default: Today (PT)
  const [custom, setCustom] = useState<{ start: string; end: string } | null>(null);

  const [stats, setStats] = useState<MirrorDashboardResponse | null>(null);
  const [debt, setDebt] = useState<DebtMixResponse | null>(null);
  const [leads, setLeads] = useState<LeadEntry[] | null>(null);
  const [leadsLimited, setLeadsLimited] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshedAt, setRefreshedAt] = useState<Date | null>(null);
  const [auto, setAuto] = useState(true);

  const time = useMemo(() => toTimeSel(presetSec, custom), [presetSec, custom]);

  // Load the client list once for the dropdown.
  useEffect(() => {
    api
      .listMirrorClients()
      .then(setClients)
      .catch(() => setClients([]));
  }, []);

  // A ref so the polling timer always calls the latest fetch (with current
  // filters) without re-arming on every keystroke.
  const fetchRef = useRef<() => void>(() => {});

  const load = useCallback(
    (showSpinner: boolean) => {
      if (showSpinner) setLoading(true);
      Promise.all([
        api.mirrorDashboard({ clientId, time }),
        api.debtMix({ clientId, time }),
        api.leads({ clientId, time, limit: LEADS_LIMIT }),
      ])
        .then(([s, dm, l]) => {
          setStats(s);
          setDebt(dm);
          setLeads(l.entries);
          setLeadsLimited(l.limited);
          setError(null);
          setRefreshedAt(new Date());
        })
        .catch((err: unknown) => {
          setError(err instanceof Error ? err.message : "Failed to load");
        })
        .finally(() => setLoading(false));
    },
    [clientId, time],
  );

  useEffect(() => {
    fetchRef.current = () => load(false);
  }, [load]);

  // Re-fetch (with spinner) whenever the filters change.
  useEffect(() => {
    load(true);
  }, [load]);

  // Auto-update polling — pauses when tab hidden or auto toggled off.
  useEffect(() => {
    if (!auto) return;
    const id = setInterval(() => {
      if (document.visibilityState === "visible") fetchRef.current();
    }, POLL_MS);
    return () => clearInterval(id);
  }, [auto]);

  const clientName = useCallback(
    (id: string | null): string => {
      if (!id) return "—";
      return clients.find((c) => c.client_id === id)?.client_name ?? id;
    },
    [clients],
  );

  const selectStyle: React.CSSProperties = {
    background: tokens.color.bg2,
    color: tokens.color.fg0,
    border: `1px solid ${tokens.color.border}`,
    borderRadius: tokens.radius.md,
    padding: `${tokens.space.sm}px ${tokens.space.md}px`,
    fontSize: tokens.font.size.sm,
    cursor: "pointer",
  };

  return (
    <div>
      <PageHeader
        title="CV API"
        subtitle="API traffic + recent pulls"
        actions={
          <div style={{ display: "flex", gap: tokens.space.sm, alignItems: "center", flexWrap: "wrap" }}>
            <select
              value={clientId}
              onChange={(e) => setClientId(e.target.value)}
              style={selectStyle}
              aria-label="API key"
            >
              <option value="all">All API keys</option>
              {clients.map((c) => (
                <option key={c.client_id} value={c.client_id}>
                  {c.client_name}
                </option>
              ))}
            </select>
            <select
              value={custom ? "custom" : String(presetSec)}
              onChange={(e) => {
                if (e.target.value === "custom") {
                  const today = new Date().toISOString().slice(0, 10);
                  setCustom({ start: today, end: today });
                } else {
                  setCustom(null);
                  setPresetSec(Number(e.target.value));
                }
              }}
              style={selectStyle}
              aria-label="Time range"
            >
              {PRESETS.map((p) => (
                <option key={p.label} value={String(p.sec)}>
                  {p.label}
                </option>
              ))}
              <option value="custom">Custom range…</option>
            </select>
            {custom && (
              <>
                <input
                  type="date"
                  value={custom.start}
                  max={custom.end}
                  onChange={(e) => setCustom({ ...custom, start: e.target.value })}
                  style={selectStyle}
                  aria-label="Start date"
                />
                <span style={{ color: tokens.color.fg2 }}>→</span>
                <input
                  type="date"
                  value={custom.end}
                  min={custom.start}
                  onChange={(e) => setCustom({ ...custom, end: e.target.value })}
                  style={selectStyle}
                  aria-label="End date"
                />
              </>
            )}
            <label
              style={{
                display: "flex",
                alignItems: "center",
                gap: 6,
                color: tokens.color.fg2,
                fontSize: tokens.font.size.sm,
                cursor: "pointer",
              }}
            >
              <input type="checkbox" checked={auto} onChange={(e) => setAuto(e.target.checked)} />
              Auto
            </label>
            <button type="button" onClick={() => load(true)} disabled={loading} style={buttonStyle("ghost", loading)}>
              {loading ? "…" : "Refresh"}
            </button>
          </div>
        }
      />

      {refreshedAt && (
        <div
          style={{
            color: tokens.color.fg2,
            fontSize: tokens.font.size.xs,
            marginBottom: tokens.space.md,
          }}
        >
          Updated{" "}
          {refreshedAt.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", second: "2-digit" })}
          {auto ? ` · auto-refresh ${POLL_MS / 1000}s` : ""}
          {" · "}
          {clientId === "all" ? "all API keys" : clientName(clientId)}
        </div>
      )}

      {error && !stats && (
        <Card>
          <EmptyState title="Failed to load" hint={error} />
        </Card>
      )}

      {/* Stat cards */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
          gap: tokens.space.lg,
          marginBottom: tokens.space.xl,
        }}
      >
        {loading && !stats ? (
          <>
            <SkeletonStat />
            <SkeletonStat />
            <SkeletonStat />
            <SkeletonStat />
          </>
        ) : stats ? (
          <>
            <StatCard label="API Calls" value={fmtCount(stats.totalCalls)} hint="total lookups" emphasis />
            <StatCard label="Cache hit rate" value={fmtPct(stats.cacheHitRate)} hint="served from credit cache" />
            <StatCard label="Upstream calls" value={fmtCount(stats.upstreamCalls)} hint="credit-pull requests" />
            <StatCard label="Upstream error rate" value={fmtPct(stats.upstreamErrorRate)} hint="failed / upstream calls" />
          </>
        ) : null}
      </div>

      {/* Debt-tier distribution cards */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
          gap: tokens.space.lg,
          marginBottom: tokens.space.xl,
        }}
      >
        {loading && !debt ? (
          <>
            <SkeletonStat />
            <SkeletonStat />
            <SkeletonStat />
          </>
        ) : debt ? (
          <>
            <StatCard
              label="Under $10k debt"
              value={fmtPct(debt.pct.below_10k)}
              hint={`${fmtCount(debt.counts.below_10k)} of ${fmtCount(debt.total)} pulls`}
            />
            <StatCard
              label="Over $10k debt"
              value={fmtPct(debt.pct.over_10k)}
              hint={`${fmtCount(debt.counts.over_10k)} pulls · qualified mid`}
            />
            <StatCard
              label="Over $20k debt"
              value={fmtPct(debt.pct.over_20k)}
              hint={`${fmtCount(debt.counts.over_20k)} pulls · qualified premium`}
              emphasis
            />
          </>
        ) : null}
      </div>

      {/* Recent leads table */}
      <Card padding={0}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: tokens.space.lg,
            borderBottom: `1px solid ${tokens.color.border}`,
          }}
        >
          <div>
            <div style={{ fontWeight: 700, fontSize: tokens.font.size.md }}>Recent pulls</div>
            <div style={{ color: tokens.color.fg2, fontSize: tokens.font.size.xs }}>
              {leads ? `${leads.length} most recent` : "loading…"}
              {leadsLimited ? ` (capped at ${LEADS_LIMIT})` : ""}
            </div>
          </div>
        </div>

        {loading && !leads ? (
          <div style={{ padding: tokens.space.lg }}>
            <Skeleton height={36} />
            <div style={{ height: 8 }} />
            <Skeleton height={36} />
            <div style={{ height: 8 }} />
            <Skeleton height={36} />
          </div>
        ) : leads && leads.length === 0 ? (
          <EmptyState title="No pulls in this range" hint="Adjust the time range or API key filter." />
        ) : leads ? (
          <div style={{ overflowX: "auto" }}>
            <table
              style={{
                width: "100%",
                borderCollapse: "separate",
                borderSpacing: 0,
                fontSize: tokens.font.size.sm,
                whiteSpace: "nowrap",
              }}
            >
              <thead>
                <tr>
                  <Th>Date / time</Th>
                  <Th>Name</Th>
                  <Th>Phone</Th>
                  <Th>Address</Th>
                  <Th>Unsecured debt</Th>
                  <Th>FICO</Th>
                  <Th>Utilization</Th>
                  <Th>Match</Th>
                  <Th>API key</Th>
                  <Th align="center">Trusted Form</Th>
                  <Th align="center">In cache</Th>
                </tr>
              </thead>
              <tbody>
                {leads.map((l, idx) => (
                  <tr
                    key={`${l.phone}-${l.pulledAtMs}-${idx}`}
                    style={{ background: idx % 2 === 0 ? "transparent" : "rgba(255,255,255,0.015)" }}
                  >
                    <Td mono>{fmtDateTime(l.pulledAt)}</Td>
                    <Td>{l.name || "—"}</Td>
                    <Td mono>{l.phone || "—"}</Td>
                    <Td>{l.address || "—"}</Td>
                    <Td>{l.debt ?? "—"}</Td>
                    <Td>{l.fico ?? "—"}</Td>
                    <Td>{l.utilization ?? "—"}</Td>
                    <Td>{l.match ?? "—"}</Td>
                    <Td>{clientName(l.clientId)}</Td>
                    <Td align="center">
                      <TrustedFormCell lead={l} />
                    </Td>
                    <Td align="center">
                      {l.rejected ? (
                        <Pill tone="danger">Rate-limited</Pill>
                      ) : l.cacheHit === true ? (
                        <Pill tone="success">Yes</Pill>
                      ) : l.cacheHit === false ? (
                        <Pill tone="warning">No</Pill>
                      ) : (
                        <span style={{ color: tokens.color.fg2 }}>—</span>
                      )}
                    </Td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : null}
      </Card>
    </div>
  );
}

function SkeletonStat() {
  return (
    <div
      style={{
        background: tokens.color.bg2,
        border: `1px solid ${tokens.color.border}`,
        borderRadius: tokens.radius.lg,
        padding: tokens.space.lg,
      }}
    >
      <Skeleton height={10} width="40%" />
      <div style={{ height: tokens.space.sm }} />
      <Skeleton height={28} width="70%" />
      <div style={{ height: tokens.space.xs }} />
      <Skeleton height={10} width="50%" />
    </div>
  );
}

// Trusted Form cell — per-request cert truthfulness + click-to-copy (PRP Task 7).
//
// The ✓/✗ MUST be per-submission-truthful so a lander that isn't sending a cert
// surfaces as ✗, never a borrowed ✓ (the whole point of the per-request build).
// When the lead holds its OWN cert URL the ✓ becomes a copy button (clipboard,
// EmbedSnippets.tsx pattern) so an operator can grab the bearer credential for an
// audit. Rate-limited (429) rows never reach the capture path, so they render a
// neutral "—" (like the In-cache cell), not a noise ✗.
//
// Own component (not inline in the row .map) so each row owns its transient
// "Copied" state without violating the rules of hooks.
function TrustedFormCell({ lead }: { lead: LeadEntry }) {
  const [copied, setCopied] = useState(false);

  if (lead.rejected) {
    return <span style={{ color: tokens.color.fg2 }}>—</span>;
  }

  if (lead.trustedForm && lead.trustedFormUrl) {
    const url = lead.trustedFormUrl;
    return (
      <button
        type="button"
        style={{
          background: "none",
          border: "none",
          padding: 0,
          cursor: "pointer",
          fontFamily: "inherit",
          fontSize: "inherit",
          color: tokens.color.teal,
          fontWeight: 700,
        }}
        title={copied ? "Copied" : "Click to copy cert URL"}
        aria-label="Copy TrustedForm cert URL"
        onClick={() => {
          // navigator.clipboard is available on the https admin origin.
          void navigator.clipboard
            .writeText(url)
            .then(() => {
              setCopied(true);
              setTimeout(() => setCopied(false), 1500);
            })
            .catch(() => {
              /* clipboard blocked — the operator can still open the row */
            });
        }}
      >
        {copied ? "Copied" : "✓"}
      </button>
    );
  }

  if (lead.trustedForm) {
    // Defensive: boolean-true with no URL (shouldn't happen post-deploy) — show
    // the plain ✓ so a present cert is never hidden behind a dead button.
    return (
      <span
        style={{ color: tokens.color.teal, fontWeight: 700 }}
        title="TrustedForm cert URL captured"
        aria-label="TrustedForm present"
      >
        ✓
      </span>
    );
  }

  return (
    <span
      style={{ color: tokens.color.fg2 }}
      title="No TrustedForm cert URL"
      aria-label="TrustedForm absent"
    >
      ✗
    </span>
  );
}

function Th({ children, align = "left" }: { children: React.ReactNode; align?: "left" | "right" | "center" }) {
  return (
    <th
      style={{
        textAlign: align,
        padding: `${tokens.space.sm}px ${tokens.space.md}px`,
        color: tokens.color.fg2,
        fontWeight: 600,
        fontSize: tokens.font.size.xs,
        textTransform: "uppercase",
        letterSpacing: "0.04em",
        borderBottom: `1px solid ${tokens.color.border}`,
      }}
    >
      {children}
    </th>
  );
}

function Td({
  children,
  align = "left",
  mono = false,
}: {
  children: React.ReactNode;
  align?: "left" | "right" | "center";
  mono?: boolean;
}) {
  return (
    <td
      style={{
        textAlign: align,
        padding: `${tokens.space.sm}px ${tokens.space.md}px`,
        color: tokens.color.fg1,
        borderBottom: `1px solid ${tokens.color.border}`,
        fontFamily: mono ? tokens.font.mono : undefined,
        fontSize: mono ? tokens.font.size.xs : undefined,
      }}
    >
      {children}
    </td>
  );
}
